import { useEffect, useState } from 'react';
import { DASHBOARD_TIME_BUTTONS } from '@bumble/app/constants';
import { useApi } from '@bumble/app/hooks/use-api';
import { Box, CardContent, Grid, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DateTime, DurationLikeObject } from 'luxon';
import { toast } from 'react-toastify';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import DashboardTimeRange from '../DashboardTimeRange';
import { Loading } from '../Loading';
import bytes from 'bytes';
import { useCpe } from './CpeContext';
import { SessionTable } from '../pcap-analyzer/SessionTable';
import { apiError } from '@bumble/app/utils/api-error';

export type EpSessions = {
  'start_time': number,
  'end_time': number,
  'src_addr': string,
  'src_port': number,
  'dst_addr': string,
  'dst_port': number,
  'proto': number,
  'tx_bytes': number,
  'tx_packets': number,
  'rx_bytes': number,
  'node_id': string,
  performance: string,
};

interface ApplicationHighlightsDashboardProps {
  selectedTab: string;
}

export const ApplicationHighlightsDashboard = ({selectedTab}: ApplicationHighlightsDashboardProps) => {
  const [timeRange, setTimeRange] = useState<DurationLikeObject>(DASHBOARD_TIME_BUTTONS[0].value);
  const [chartHitsData, setChartHitsData] = useState<any[]>([]);
  const [chartBytesData, setChartBytesData] = useState<any[]>([]);
  const [totalApps, setTotalApps] = useState<number>(0);
  const [totalVisits, setTotalVisits] = useState<number>(0);
  const [totalBytes, setTotalBytes] = useState<number>(0);
  const [percentage, setPercentage] = useState<number>(0);
  const [sessions, setSessions] = useState<EpSessions[]>([]);
  const [isLoading2, setIsLoading2] = useState<boolean>(false);
  const { cpe, setCpe } = useCpe();
  const api = useApi();

  let oldCpeName: string, oldTimeRange: DurationLikeObject;

  const { isLoading, data, refetch, fetchStatus, isRefetching } = useQuery(
    ['graph_data'],
    api.dashboardGetNodeAppHighlightApiV1DashboardGetNodeAppHighlightGet.bind(null, {
      node_id: cpe.nodeId,
      node_name: cpe.cpeName,
      start_time: DateTime.now().minus(timeRange).toISO(),
      end_time: DateTime.now().toISO(),
    }),
    { enabled: false },
  );

  const getNodeSessionCall = useMutation(api.dashboardGetNodeSessionsApiV1DashboardGetNodeSessionsGet, {
    onSuccess: (data) => {
      const newSessions = (data as { data: { sessions: EpSessions[] } }).data.sessions;
      setSessions(newSessions.map((item: EpSessions, i: number) => ({
        ...item,
        id: `${item.node_id}_${item.start_time}_${i}`,
      })));
      setPercentage(
        newSessions.length ? newSessions.filter((s: EpSessions) => s.performance !== 'good').length * 100 / newSessions.length
      : 0);
      setIsLoading2(false);
    },
    onError(e) {
      apiError(e);
      setIsLoading2(false);
    }
  });

  const handleCpeChange = (val : any) => {
    if (val) {
      setCpe({
        cpeName: val.label,
        nodeId: val.id
      })
    }
  };

  const handleTimeChange = (e: React.MouseEvent<HTMLElement>, val: DurationLikeObject) => {
    if (val) {
      setTimeRange(val);
    }
  };

  const handleRefreshClick = () => {
    if (cpe.cpeName && timeRange) {
      setIsLoading2(true);
      refetch();
      getNodeSessionCall.mutate({
        node_id: cpe.nodeId,
        node_name: cpe.cpeName,
        start_time: DateTime.now().minus(timeRange).toISO(),
        end_time: DateTime.now().toISO(),
      });
      toast.success('Refreshed data.');
    }
  };

  useEffect(() => {
    if (cpe.cpeName && timeRange && (cpe.cpeName !== oldCpeName || timeRange !== oldTimeRange)) {
      setIsLoading2(true);
      refetch();
      getNodeSessionCall.mutate({
        node_id: cpe.nodeId,
        node_name: cpe.cpeName,
        start_time: DateTime.now().minus(timeRange).toISO(),
        end_time: DateTime.now().toISO(),
      });
      oldTimeRange = timeRange;
      oldCpeName = cpe.cpeName;
    }
  }, [timeRange, cpe]);

  useEffect(() => {
    if (data?.data) {
      const {
        top_app_by_hits: topAppByHits,
        top_app_by_bytes: topAppByBytes,
        total_apps,
        total_visits,
        total_bytes,
      } = data?.data as {
        top_app_by_hits: Array<{ app_name: string, hits: number }>,
        top_app_by_bytes: Array<{ app_name: string, bytes: number }>,
        total_apps: number,
        total_visits: number,
        total_bytes: number,
      };
      setTotalApps(total_apps);
      setTotalBytes(total_bytes);
      setTotalVisits(total_visits);
      setChartHitsData(topAppByHits.map(n => ({
        name: n.app_name,
        hits: n.hits
      })));
      setChartBytesData(topAppByBytes.map(n => ({
        name: n.app_name,
        hits: n.bytes
      })));
    }
  }, [data]);

  const CustomAxisLabel = ({ x, y, payload } : any) => {
    const [labels, setLabels] = useState<string[]>([]);
    const splitIntoChunks = (input: string, length: number) => {
      const result = [];
      for (let i = 0; i < input.length; i += length) {
        result.push(input.slice(i, i + Math.min(input.length, length)));
      }
      return result;
    };

    useEffect(() => {
      const ary = splitIntoChunks(payload.value, 10);
      if (ary.length > 2) {
        setLabels([ary[0], `${ary[1].slice(0, 7)}...`]);
      } else {
        setLabels([...ary]);
      }
    }, [payload]);

    return (
      <text x={x} y={y} textAnchor="end" fill="#666" transform={`rotate(-35, ${x}, ${y})`}>
        {labels.length > 0 && <tspan x={x} dy="5">{labels[0]}</tspan>}
        {labels.length > 1 && <tspan x={x} dy="20">{labels[1]}</tspan>}
      </text>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 0, py: 5, pb: 0 }}>
        <DashboardTimeRange
          timeValues={DASHBOARD_TIME_BUTTONS}
          selectedTime={timeRange}
          onCpeChange={handleCpeChange}
          onTimeChange={handleTimeChange}
          onRefresh={handleRefreshClick}
          selectedTab={selectedTab}
        />
      </Box>
      <Box>
        <Grid container spacing={5} sx={{ marginTop: 1, textAlign: 'center' }}>
          <Grid item xs={4} md={4}>
            <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }}>
              <Typography variant="h5">Total Apps</Typography>
              <Typography variant="h1" sx={{ marginTop: 2 }}>{totalApps.toLocaleString('en-US')}</Typography>
            </CardContent>
          </Grid>
          <Grid item xs={4} md={4}>
            <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }}>
              <Typography variant="h5">Total Visits</Typography>
              <Typography variant="h1" sx={{ marginTop: 2 }}>{totalVisits.toLocaleString('en-US')}</Typography>
            </CardContent>
          </Grid>
          <Grid item xs={4} md={4}>
            <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }}>
              <Typography variant="h5">Total Bytes</Typography>
              <Typography variant="h1" sx={{ marginTop: 2 }}>{bytes.format(totalBytes, {unitSeparator: ' '})}</Typography>
            </CardContent>
          </Grid>
        </Grid>
        <Grid container spacing={5} sx={{ marginTop: 1 }}>
          <Grid item sm={12} md={6}>
            <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }}>
              <Typography color="primary" sx={{ mb: 2 }}>Top Apps by Hits</Typography>
              {((isLoading || isRefetching) && fetchStatus !== 'idle') ? <Loading height={200} /> :
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={chartHitsData}
                    margin={{ top: 5, right: 20, left: 30, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-35}
                      height={chartHitsData.length ? 80 : 0}
                      textAnchor="end"
                      tick={<CustomAxisLabel />}
                    />
                    <YAxis
                      orientation="left"
                      type="number"
                      width={30}
                      domain={chartHitsData.length ? undefined : [0, 100]}
                      tickFormatter={(value: number) => new Intl.NumberFormat('en-US', {
                        notation: 'compact',
                        compactDisplay: 'short',
                      }).format(value)}
                    />
                    {chartHitsData.length > 0 && <Tooltip />}
                    <Bar dataKey="hits" fill="rgb(95, 70, 126)" name="Top Apps By Hits" />
                  </BarChart>
                </ResponsiveContainer>}
            </CardContent>
          </Grid>
          <Grid item sm={12} md={6}>
            <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }}>
              <Typography color="primary" sx={{ mb: 2 }}>Top Apps by Bytes</Typography>
              {((isLoading || isRefetching) && fetchStatus !== 'idle') ? <Loading height={200} /> :
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={chartBytesData}
                    margin={{ top: 5, right: 20, left: 50, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      textAnchor="end"
                      angle={-35}
                      tick={<CustomAxisLabel />}
                      height={chartBytesData.length ? 80 : 0}
                    />
                    <YAxis
                      orientation="left"
                      type="number"
                      width={30}
                      domain={chartBytesData.length ? undefined : [0, 100]}
                      tickFormatter={(value: number) => bytes.format(value)}
                    />
                    {chartBytesData.length && <Tooltip />}
                    <Bar dataKey="hits" fill="rgb(110, 159, 206)" name="Top Apps By Bytes" />
                  </BarChart>
                </ResponsiveContainer>
              }
            </CardContent>
          </Grid>
          <Grid item xs={12} md={12}>
            <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2 }}>
              <Typography color="primary" sx={{ mb: 2 }}>App Flows & Performance Analysis</Typography>
              {isLoading2 ? <Loading height={200} /> :
                <SessionTable
                  percentage={percentage}
                  sessions={sessions}
                  title={cpe.cpeName || ''}
                />
              }
            </CardContent>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
